import React, { useState, useRef } from 'react';
import { apiService } from '../services/apiService';
import { notificationService } from '../services/notificationService';
import { UploadProgress } from '../types';

interface PasswordPrompt {
  file: File;
  fileName: string;
}

interface UploadProps {
  onUploadComplete: () => void;
}

const Upload: React.FC<UploadProps> = ({ onUploadComplete }) => {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [passwordPrompt, setPasswordPrompt] = useState<PasswordPrompt | null>(null);
  const [password, setPassword] = useState('');
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    uploadFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      uploadFiles(files);
    }
  };

  const handleUploadAreaClick = () => {
    fileInputRef.current?.click();
  };

  const validateFile = (file: File): { isValid: boolean; error?: string } => {
    const validTypes = ['pdf', 'docx', 'pptx', 'xlsx'];
    const extension = file.name.split('.').pop()?.toLowerCase();
    const maxSize = 100 * 1024 * 1024; // 100MB

    if (!extension || !validTypes.includes(extension)) {
      return {
        isValid: false,
        error: `${file.name}: Unsupported file type. Please use PDF, DOCX, PPTX, or XLSX.`
      };
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `${file.name}: File too large. Maximum size is 100MB.`
      };
    }

    return { isValid: true };
  };


  const handlePasswordSubmit = async () => {
    if (!passwordPrompt || !password.trim()) return;
    
    setIsCheckingPassword(true);
    
    try {
      // Try to upload with password
      const formData = new FormData();
      formData.append('file', passwordPrompt.file);
      formData.append('password', password);
      
      await apiService.uploadDocumentWithPassword(formData, (newProgress) => {
        // Update progress for this file
        setUploadProgress(prev =>
          prev.map(item =>
            item.fileName === passwordPrompt.fileName
              ? { ...item, progress: 80 + (Math.min(newProgress, 80) * 0.2) } // Cap at 99% during password upload
              : item
          )
        );
      });

      // Mark as completed
      setUploadProgress(prev =>
        prev.map(item =>
          item.fileName === passwordPrompt.fileName
            ? { ...item, status: 'processing', progress: 80 }
            : item
        )
      );

      notificationService.info(`⏳ ${passwordPrompt.fileName} uploaded. Processing...`, 3000);
      
      // Clear password prompt
      setPasswordPrompt(null);
      setPassword('');
      
      // Call completion callback
      onUploadComplete();
      
      // Clear progress after a delay
      setTimeout(() => {
        setUploadProgress([]);
      }, 3000);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error: any) {
      console.error('Password upload error:', error);
      
      // Check for specific error cases
      const errorDetail = error.response?.data?.detail || '';
      
      if (error.response?.status === 401) {
        notificationService.error('Incorrect password. Please try again.');
        setPassword(''); // Just clear the password field
        // Keep progress at 80% and status as failed with the password error message
        setUploadProgress(prev =>
          prev.map(item =>
            item.fileName === passwordPrompt.fileName
              ? { ...item, progress: 80, status: 'failed', error: 'PDF is password encrypted. Please enter the password.' }
              : item
          )
        );
      } else if (error.response?.status === 400 && errorDetail.includes("Failed to process PDF")) {
        // This is likely an incorrect password case
        notificationService.error('Failed to process PDF. Please try again with the correct password.');
        // Don't clear the password prompt, let the user try again
        setPassword(''); // Just clear the password field
        // Keep progress at 80% and status as failed with the password error message
        setUploadProgress(prev =>
          prev.map(item =>
            item.fileName === passwordPrompt.fileName
              ? { ...item, progress: 80, status: 'failed', error: 'PDF is password encrypted. Please enter the password.' }
              : item
          )
        );
      } else {
        // Mark as failed for other errors
        setUploadProgress(prev =>
          prev.map(item =>
            item.fileName === passwordPrompt.fileName
              ? {
                  ...item,
                  status: 'failed',
                  progress: 0, // Reset progress for other failures
                  error: error.response?.data?.detail || error.message || 'Upload failed'
                }
              : item
          )
        );
        
        notificationService.error(`❌ Failed to upload ${passwordPrompt.fileName}: ${error.response?.data?.detail || error.message}`);
        setPasswordPrompt(null);
        setPassword('');
        
        // Clear progress after a delay
        setTimeout(() => {
          setUploadProgress([]);
        }, 3000);
        
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } finally {
      setIsCheckingPassword(false);
    }
  };

  const handlePasswordCancel = () => {
    if (passwordPrompt) {
      // Mark as failed
      setUploadProgress(prev => 
        prev.map(item => 
          item.fileName === passwordPrompt.fileName 
            ? { ...item, status: 'failed', error: 'Upload cancelled - password required' } 
            : item
        )
      );
      
      // Clear progress after a delay
      setTimeout(() => {
        setUploadProgress([]);
      }, 3000);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    
    setPasswordPrompt(null);
    setPassword('');
  };

  const uploadFiles = async (files: File[]) => {
    if (isUploading) return;

    let passwordPromptTriggered = false;

    // Validate files
    const validFiles: File[] = [];
    for (const file of files) {
      const validation = validateFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        notificationService.warning(validation.error!);
      }
    }

    if (validFiles.length === 0) {
      notificationService.error('No valid files selected');
      return;
    }

    setIsUploading(true);
    
    // Initialize progress tracking
    const initialProgress: UploadProgress[] = validFiles.map(file => ({
      fileName: file.name,
      progress: 0,
      status: 'uploading'
    }));
    setUploadProgress(initialProgress);

    try {
      // Upload files sequentially
      for (let i = 0; i < validFiles.length && !passwordPromptTriggered; i++) {
        const file = validFiles[i];
        
        try {
          await apiService.uploadDocument(file, (currentProgress) => {
            setUploadProgress(prev =>
              prev.map((item, index) =>
                index === i ? { ...item, progress: Math.min(currentProgress, 80) } : item // Cap at 99% during upload
              )
            );
          });

          // After successful upload, set to processing state
          setUploadProgress(prev =>
            prev.map((item, index) =>
              index === i ? { ...item, status: 'processing', progress: 80 } : item
            )
          );

          notificationService.info(`⏳ ${file.name} uploaded. Processing...`, 3000);

        } catch (error: any) {
          console.error('Upload error:', error);
          console.log('Error response:', error.response);
          
          // Check if this is a password-protected PDF error
          const isPdfPasswordError =
            error.response?.status === 400 &&
            (error.response?.data?.detail?.includes("PDF is password encrypted") ||
             error.response?.data?.detail?.includes("upload-with-password"));
          
          if (isPdfPasswordError) {
            console.log('Password protected PDF detected, showing password prompt');
            
            // Set password prompt and stop processing other files
            setPasswordPrompt({ file, fileName: file.name });
            passwordPromptTriggered = true;
            
            // Mark current file as needing password, ensure progress is 80%
            setUploadProgress(prev =>
              prev.map((item, index) =>
                index === i ? {
                  ...item,
                  progress: 80, // Stop at 80% for password prompt
                  status: 'failed', // Use existing 'failed' status
                  error: 'PDF is password encrypted. Please enter the password.'
                } : item
              )
            );
            
            break; // Stop processing other files until password is provided
          } else {
            // Mark as failed for other errors
            setUploadProgress(prev => 
              prev.map((item, index) => 
                index === i ? { 
                  ...item, 
                  status: 'failed', 
                  error: error.response?.data?.detail || error.message || 'Upload failed'
                } : item
              )
            );

            notificationService.error(`❌ Failed to upload ${file.name}: ${error.response?.data?.detail || error.message}`);
          }
        }
      }

      // Only call completion callback if no password prompt was triggered
      if (!passwordPromptTriggered) {
        onUploadComplete();
        
        // Clear progress after a delay
        setTimeout(() => {
          setUploadProgress([]);
        }, 3000);

        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

    } finally {
      // Always set isUploading to false
      setIsUploading(false);
    }
  };

  return (
    <div style={{
      background: 'var(--bg-color)',
      borderRadius: '0.75rem',
      border: '1px solid var(--border-color)',
      boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
      margin: '1.5rem'
    }}>
      <div style={{
        padding: '1.5rem 1.5rem 0 1.5rem',
        borderBottom: '1px solid var(--border-color)'
      }}>
        <h6 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600 }}>Upload Documents</h6>
      </div>
      <div style={{ padding: '1.5rem' }}>
        {/* Enhanced Upload Area */}
        <div
          onClick={handleUploadAreaClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${isDragOver ? 'var(--text-primary)' : 'var(--border-color)'}`,
            borderRadius: '1rem',
            padding: '4rem 2rem',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            background: isDragOver ? 'var(--bg-secondary)' : 'var(--bg-secondary)',
            transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
            boxShadow: isDragOver ? '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' : '0 1px 3px 0 rgb(0 0 0 / 0.1)'
          }}
        >
          <div style={{ marginBottom: '2rem' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                background: isDragOver ? 'var(--text-primary)' : 'var(--bg-secondary)',
                color: isDragOver ? 'var(--bg-color)' : 'var(--text-primary)',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem',
                transition: 'all 0.3s ease'
              }}
            >
              <i 
                className="bi bi-cloud-arrow-up" 
                style={{ fontSize: '2rem' }}
              ></i>
            </div>
          </div>
          
          <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
            {isDragOver ? 'Drop your files here' : 'Upload Documents'}
          </h4>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
            Drag and drop your files here, or click to browse
          </p>
          
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <span style={{ 
              background: 'rgba(239, 68, 68, 0.1)', 
              color: '#ef4444',
              padding: '0.5rem 1rem',
              fontSize: '0.75rem',
              borderRadius: '0.375rem'
            }}>
              <i className="bi bi-file-earmark-pdf" style={{ marginRight: '0.25rem' }}></i>
              PDF
            </span>
            <span style={{ 
              background: 'rgba(59, 130, 246, 0.1)', 
              color: '#3b82f6',
              padding: '0.5rem 1rem',
              fontSize: '0.75rem',
              borderRadius: '0.375rem'
            }}>
              <i className="bi bi-file-earmark-word" style={{ marginRight: '0.25rem' }}></i>
              DOCX
            </span>
            <span style={{
              background: 'rgba(245, 158, 11, 0.1)',
              color: '#f59e0b',
              padding: '0.5rem 1rem',
              fontSize: '0.75rem',
              borderRadius: '0.375rem'
            }}>
              <i className="bi bi-file-earmark-ppt" style={{ marginRight: '0.25rem' }}></i>
              PPTX
            </span>
            <span style={{
              background: 'rgba(34, 197, 94, 0.1)',
              color: '#22c55e',
              padding: '0.5rem 1rem',
              fontSize: '0.75rem',
              borderRadius: '0.375rem'
            }}>
              <i className="bi bi-file-earmark-excel" style={{ marginRight: '0.25rem' }}></i>
              XLSX
            </span>
          </div>
          
          <small style={{ color: 'var(--text-secondary)' }}>Maximum file size: 100MB per file</small>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.pptx,.xlsx"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {/* Upload Progress */}
        {uploadProgress.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <h6 style={{ marginBottom: '1rem', color: 'var(--text-primary)', fontWeight: 600 }}>Upload Progress</h6>
            {uploadProgress.map((item, index) => (
              <div key={index} style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className={`bi ${item.status === 'completed' ? 'bi-check-circle' : (item.status === 'failed' && item.error?.includes('password encrypted')) ? 'bi-lock' : item.status === 'failed' ? 'bi-x-circle' : item.status === 'processing' ? 'spinner-border spinner-border-sm' : 'bi-arrow-up-circle'}`} style={{
                      marginRight: '0.5rem',
                      color: item.status === 'completed' ? '#10b981' : (item.status === 'failed' && item.error?.includes('password encrypted')) ? '#f59e0b' : item.status === 'failed' ? '#ef4444' : item.status === 'processing' ? 'var(--primary-color)' : '#1e293b'
                    }}></i>
                    <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{item.fileName}</span>
                  </div>
                  <small style={{ color: 'var(--text-secondary)' }}>
                    {item.status === 'uploading' || item.status === 'processing' || (item.status === 'failed' && item.error?.includes('password encrypted')) ? `${Math.round(item.progress)}%` : item.status}
                  </small>
                </div>
                
                <div style={{
                  height: '6px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div
                    style={{
                      width: `${item.progress}%`,
                      height: '100%',
                      background: item.status === 'completed' ? 'var(--success-color)' : (item.status === 'failed' && item.error?.includes('password encrypted')) ? 'var(--warning-color)' : item.status === 'failed' ? 'var(--danger-color)' : item.status === 'processing' ? 'var(--primary-color)' : 'var(--primary-color)',
                      transition: 'width 0.3s ease'
                    }}
                  ></div>
                </div>
                
                {item.error && (
                  <small style={{ color: 'var(--danger-color)', marginTop: '0.25rem', display: 'block' }}>
                    <i className="bi bi-exclamation-triangle" style={{ marginRight: '0.25rem' }}></i>
                    {item.error}
                  </small>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upload Instructions */}
        {uploadProgress.length === 0 && !isUploading && (
          <div style={{ marginTop: '1.5rem' }}>
            <h6 style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontWeight: 600 }}>Supported File Types</h6>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
                <i className="bi bi-file-earmark-pdf" style={{ fontSize: '2rem', color: 'var(--danger-color)', marginRight: '1rem' }}></i>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>PDF Documents</div>
                  <small style={{ color: 'var(--text-secondary)' }}>Portable Document Format</small>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
                <i className="bi bi-file-earmark-word" style={{ fontSize: '2rem', color: 'var(--info-color)', marginRight: '1rem' }}></i>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Word Documents</div>
                  <small style={{ color: 'var(--text-secondary)' }}>Microsoft Word (.docx)</small>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
                <i className="bi bi-file-earmark-ppt" style={{ fontSize: '2rem', color: 'var(--warning-color)', marginRight: '1rem' }}></i>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>PowerPoint</div>
                  <small style={{ color: 'var(--text-secondary)' }}>Microsoft PowerPoint (.pptx)</small>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
                <i className="bi bi-file-earmark-excel" style={{ fontSize: '2rem', color: 'var(--success-color)', marginRight: '1rem' }}></i>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Excel Spreadsheet</div>
                  <small style={{ color: 'var(--text-secondary)' }}>Microsoft Excel (.xlsx)</small>
                </div>
              </div>
            </div>
            
            <div style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: '0.5rem', background: 'var(--info-bg)' }}>
              <h6 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', fontWeight: 600 }}>
                <i className="bi bi-info-circle" style={{ marginRight: '0.5rem' }}></i>
                Processing Information
              </h6>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <li>Documents are automatically processed after upload</li>
                <li>Processing time varies based on document size and complexity</li>
                <li>You'll be notified when processing is complete</li>
                <li>Processed documents become available for AI analysis</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Password Modal */}
      {passwordPrompt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'var(--bg-color)',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 10px 10px -5px rgb(0 0 0 / 0.04)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'var(--warning-bg)',
                color: 'var(--warning-color)',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem'
              }}>
                <i className="bi bi-lock" style={{ fontSize: '1.5rem' }}></i>
              </div>
              <h5 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600, marginBottom: '0.5rem' }}>
                Password Protected PDF
              </h5>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.875rem' }}>
                This PDF requires a password to access. Please enter the password to continue.
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--text-primary)',
                marginBottom: '0.5rem',
                display: 'block'
              }}>
                File: {passwordPrompt.fileName}
              </label>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--text-primary)',
                marginBottom: '0.5rem',
                display: 'block'
              }}>
                Password:
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                placeholder="Enter PDF password"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  background: 'var(--bg-color)',
                  color: 'var(--text-primary)'
                }}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handlePasswordCancel}
                disabled={isCheckingPassword}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--bg-color)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  cursor: isCheckingPassword ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  opacity: isCheckingPassword ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordSubmit}
                disabled={!password.trim() || isCheckingPassword}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: !password.trim() || isCheckingPassword ? 'var(--border-color)' : 'var(--text-primary)',
                  color: !password.trim() || isCheckingPassword ? 'var(--text-secondary)' : 'var(--bg-color)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: !password.trim() || isCheckingPassword ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {isCheckingPassword && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid currentColor',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                )}
                {isCheckingPassword ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Upload;
